// import lib(s)
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import styles from "./TaskList.module.css"

// @mui
import { GridActionsCellItem } from "@mui/x-data-grid-premium"
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'

// Custom component
import { KcTable } from "../../components/common/kc-table"
import {
	SUPPORTED_LOCALE,
	LOCALE_DEFAULT_TEXT,
	IconComboCellRenderer,
} from "../../components/common/kc-table"

import { useAxiosPrivate, useDocumentTitle } from "../../hooks"
import { PAGE_TITLE } from "../../constants/constants"

import Layout from "../../layouts/common/Layout"
import PlaceholderText from "../../components/common/SampleText"
import Loader from "../../components/common/Loader"
import toast from "react-hot-toast"

import { METHODS, errorHandler } from "../../hooks/useFetch"

import { API_URL } from "../../api/constants"

import { useNavigate } from "react-router-dom"
import ROUTES from "../../route/routes"
import { CONST_VARIANT } from "../../components/common/kc-table/constants"

import VisibilityIcon from "@mui/icons-material/VisibilityOutlined"
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from "@mui/icons-material/ModeEditOutlined"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"

import { setRawData, setCurrentTask, setCurrentTaskRecordings, setCurrentRecordingId, init, setAnnotatorState } from '../Annotator/AnnotatorSlice'
import universalFormatter, { resolveEcgType } from "../../utils/universalFormatter"
import { refreshTaskWaveforms } from "../../utils/refreshTaskWaveforms"

const TaskList = () => {

	useDocumentTitle(PAGE_TITLE.TASK_LIST)
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const [loading, setLoading] = useState(true)
	const [downloadingTasks, setDownloadingTasks] = useState({})
	const [error, setError] = useState(null)
	const [tasks, setData] = useState(null)
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
	const [taskToDelete, setTaskToDelete] = useState(null)
	const [confirmText, setConfirmText] = useState('')
	// State for the "download with refresh?" prompt. `refreshProgress` is null
	// while the user is deciding, {completed, total} while the refresh is
	// running, and the dialog stays open through the whole flow so we can
	// surface progress + errors before it closes.
	const [downloadDialogTask, setDownloadDialogTask] = useState(null)
	const [refreshProgress, setRefreshProgress] = useState(null)
	const [refreshError, setRefreshError] = useState(null)

	const axiosPrivate = useAxiosPrivate()
	const currentUserEmail = useSelector((state) => state.auth?.user?.email)

	const fetchTasks = async () => {
		const PATH = API_URL.TASKS
		try {
			const response = await axiosPrivate[METHODS.GET.toLowerCase()](PATH)
			if (response.data.length === 0) {
				toast.error("No Tasks Found")
				throw new Error("No Tasks Found")
			}
			const tasksWithPlaceholders = response.data.map(task => ({ ...task, totalRecordings: undefined, seenRecordings: undefined }))
			setData(tasksWithPlaceholders)
			setError(null)
			return tasksWithPlaceholders
		} catch (error) {
			setError(errorHandler(error))
		} finally {
			setLoading(false)
		}
	}

	const fetchProgress = async (taskId) => {
		try {
			const progressRes = await axiosPrivate[METHODS.GET.toLowerCase()](`taskProgress?task_id=${taskId}`)
			setData(prev => prev.map(task =>
				task.id === taskId
					? { ...task, totalRecordings: progressRes.data.totalRecordings, seenRecordings: progressRes.data.seenRecordings }
					: task
			))
		} catch {
			setData(prev => prev.map(task =>
				task.id === taskId
					? { ...task, totalRecordings: 0, seenRecordings: 0 }
					: task
			))
		}
	}

	useEffect(() => {
		fetchTasks().then(tasks => {
			tasks?.forEach(task => fetchProgress(task.id))
		})
        window.sidebar.open()
		// eslint-disable-next-line
	}, [])

	const renderDateCell = (params) => {
		const { value } = params
		const date = new Date(value)

		return (
			<div onClick={() => renderTask(params)} className={styles.rowClass}>
				{date.toLocaleDateString()} {date.toLocaleTimeString()}
			</div>
		)
	}

	const renderCell = (params) => {
		const { value } = params

		return (
			<div onClick={() => renderTask(params)} className={styles.rowClass}>
				{value}
			</div>
		)
	}

	const columns = [
		{ field: "id", headerName: "Task ID", renderCell, minWidth: 100, flex: 1 },
		{ field: "name", headerName: "Task Name", renderCell, minWidth: 300, flex: 1 },
		{ field: "type", headerName: "Task Type", renderCell,  minWidth: 150, flex: 1 },
		{
			field: "progress",
			headerName: "Progress",
			minWidth: 120,
			flex: 1,
			renderCell: (params) => {
				const { row } = params
				if (row.totalRecordings === undefined) {
					return "Loading..."
				}
				return `${row.seenRecordings} / ${row.totalRecordings}`
			}
		},
		{
			field: "createdAt",
			headerName: "Created at",
			minWidth: 100,
			renderCell: renderDateCell,
			flex: 1,
		},
		{
			field: "updatedAt",
			headerName: "Updated at",
			minWidth: 100,
			renderCell: renderDateCell,
			flex: 1,
		},
		{
			field: "actions",
			headerName: "Actions",
			width: 100,
			disableExport: true,
			filterable: false,
			renderCell: (params) => {
				const { id } = params
				return [
					<GridActionsCellItem
						sx={{
							padding: 1.25,
							color: "inherit",
							"& .MuiSvgIcon-root": {
								fontSize: "1.6rem",
							},
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 999,
						}}
						icon={downloadingTasks[id] ? <CircularProgress size={24} color="inherit" /> : <FileDownloadOutlinedIcon />}
						label="DOWNLOAD"
						key={`${id}-DOWNLOAD`}
						onClick={() => openDownloadDialog(params.row)}
						disabled={downloadingTasks[id]}
						color="inherit"
					/>,
					<GridActionsCellItem
						sx={{
							padding: 1.25,
							color: "inherit",
							"& .MuiSvgIcon-root": {
								fontSize: "1.6rem",
							},
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 999,
						}}
						icon={<DeleteIcon />}
						label="DELETE"
						key={`${id}-DELETE`}
						onClick={() => {
							setTaskToDelete(params.row)
							setDeleteDialogOpen(true)
						}}
						color="inherit"
					/>
				]
			},
		},
	]

	const downloadTaskZip = async (taskId) => {
		// The backend specifically expects the 'auth' query parameter for this endpoint
		// (as noted in the backend docs: &auth=localStorage.getItem("id_token") or similar)
		const usrStr = window.localStorage.getItem("usr")
		const token = usrStr ? JSON.parse(usrStr).token : ""

		const response = await axiosPrivate.get("taskResults", {
			params: { task_id: taskId, auth: token },
			responseType: "blob",
		})

		const blob = new Blob([response.data], { type: "application/zip" })
		const objectUrl = URL.createObjectURL(blob)
		const link = document.createElement("a")
		link.href = objectUrl
		link.download = `taskResults_${taskId}.zip`
		document.body.appendChild(link)
		link.click()
		link.parentNode.removeChild(link)
		setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
	}

	const openDownloadDialog = (task) => {
		setDownloadDialogTask(task)
		setRefreshProgress(null)
		setRefreshError(null)
	}

	const closeDownloadDialog = () => {
		// Guard against closing mid-refresh (the Dialog's onClose still fires
		// on backdrop clicks even when actions are disabled).
		if (refreshProgress && !refreshError) return
		setDownloadDialogTask(null)
		setRefreshProgress(null)
		setRefreshError(null)
	}

	const handleDownloadAsIs = async () => {
		const task = downloadDialogTask
		if (!task) return
		const taskId = task.id
		setDownloadDialogTask(null)
		try {
			setDownloadingTasks(prev => ({ ...prev, [taskId]: true }))
			await downloadTaskZip(taskId)
			toast.success("Task results downloaded successfully")
		} catch (error) {
			toast.error("Failed to download task results")
			console.error(error)
		} finally {
			setDownloadingTasks(prev => ({ ...prev, [taskId]: false }))
		}
	}

	const handleRefreshAndDownload = async () => {
		const task = downloadDialogTask
		if (!task) return
		const taskId = task.id
		setRefreshError(null)
		setRefreshProgress({ completed: 0, total: 0 })
		setDownloadingTasks(prev => ({ ...prev, [taskId]: true }))
		try {
			const result = await refreshTaskWaveforms(axiosPrivate, taskId, currentUserEmail, {
				onProgress: ({ completed, total }) => setRefreshProgress({ completed, total }),
			})
			if (!result.ok) {
				setRefreshError({
					message: `Refresh aborted at ${result.completed}/${result.total} recordings.`,
					phase: result.phase,
					recordingId: result.recordingId,
				})
				return
			}
			// All recordings refreshed — proceed straight to the ZIP download.
			await downloadTaskZip(taskId)
			toast.success(`Refreshed ${result.refreshed} recording(s) and downloaded ZIP`)
			setDownloadDialogTask(null)
			setRefreshProgress(null)
		} catch (error) {
			setRefreshError({ message: "Unexpected error during refresh/download.", raw: error })
			console.error(error)
		} finally {
			setDownloadingTasks(prev => ({ ...prev, [taskId]: false }))
		}
	}

	const confirmDelete = async () => {
		setDeleteDialogOpen(false)
		setLoading(true)
		try {
			const { data: CurrentTask } = await axiosPrivate[METHODS.DELETE.toLowerCase()](`task?task_id=${taskToDelete.id}`)
			console.log(CurrentTask);
			toast.success("Task Deleted Successfully");
			setError(null)
		} catch (error) {
			toast.error("Failed to delete task")
			console.log(error);
			setError(errorHandler(error))
		} finally {
			setTaskToDelete(null)
			setConfirmText('')
			fetchTasks()
		}
	}

	const handleCloseDeleteDialog = () => {
		setDeleteDialogOpen(false)
		setTaskToDelete(null)
		setConfirmText('')
	}

	const LOCALE = {
		...LOCALE_DEFAULT_TEXT.enUS,
		noRowsLabel: "No Facilities Found...",
	}

	const renderTask = async (data) => {
		const { row } = data
		dispatch(init())
		dispatch(setCurrentTask(row))
		setLoading(true)
		try {
			// Fetch All recordingList of this Task
			const start = performance.now();
			const { data: TaskRecordingList } = await axiosPrivate[METHODS.GET.toLowerCase()](`recordings?task_id=${row.id}`)
			if (TaskRecordingList.length === 0) {
				toast.error("No Recordings Found")
				return
			}
			dispatch(setCurrentTaskRecordings(TaskRecordingList))

			// Fetch Recording Data of first recording
			const { data: RecordingData } = await axiosPrivate[METHODS.GET.toLowerCase()](`recording?recording_id=${TaskRecordingList[0].id}`)
			dispatch(setRawData(RecordingData))
			dispatch(setCurrentRecordingId(TaskRecordingList[0].id))
			console.log(RecordingData);
			let uniEcg;
			if (RecordingData.data.hasOwnProperty("type")) {
				uniEcg = universalFormatter(RecordingData.data, resolveEcgType(RecordingData.data, RecordingData.data.type));
			} else {
				uniEcg = universalFormatter(RecordingData.data, "turk");
			}
			if (uniEcg.error !== null) { toast.error(uniEcg.error); return; }
			console.log(uniEcg);
			const leadCount = Object.keys(uniEcg.filtered.samples).length
			const end = performance.now();
			// Calculate the elapsed time
			const elapsed = end - start;
			console.log(`Opening took ${elapsed} millisecond`);
			toast.success(`Loaded ${leadCount}L Turk Recording in ${elapsed.toFixed(1)}ms!`)
			dispatch(setAnnotatorState({ ...uniEcg, leadCount }))
			window.sidebar.close()
			setLoading(false)
			navigate(`/tool/${row.id}/${TaskRecordingList[0].id}`)

			setError(null)
		} catch (error) {
			toast.error("Error fetching Task Recordings")
			console.log(error);
			setError(errorHandler(error))
		} finally {
			setLoading(false)
		}
	}

	return (
		<Layout.CenterWrapper>
			{loading || error?.status === 421 ? (
				<Loader />
			) : error ? (
				<PlaceholderText>Error</PlaceholderText>
			) : (
				<React.Fragment>
					<div className={styles.gridPanel}>
						<KcTable
							initialState={{
								sorting: {
									sortModel: [{ field: "createdAt", sort: "desc" }],
								},
							}}
							rows={tasks}
							columns={columns}
							defaultLocaleLanguage={SUPPORTED_LOCALE.English}
							defaultLocaleText={LOCALE}
							pagination
							layout={CONST_VARIANT.KC_DEFAULT}
							disableSelectionOnClick
							// disableDensitySelector
							// disableColumnFilter
							// disableColumnSelector
							// disableTableExport

							// getRowClassName={(params) => {return styles.rowClass}}
							autoHeight={tasks.length <= 10}
						/>
					</div>
				</React.Fragment>
			)}

			<Dialog
				open={!!downloadDialogTask}
				onClose={closeDownloadDialog}
				aria-labelledby="download-task-dialog-title"
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle id="download-task-dialog-title">
					Download task results
				</DialogTitle>
				<DialogContent>
					{refreshProgress && !refreshError ? (
						<>
							<Typography variant="body1" sx={{ mb: 2 }}>
								Refreshing recording {refreshProgress.completed}
								{refreshProgress.total ? ` of ${refreshProgress.total}` : ""}…
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Please don't close this window. The ZIP will download automatically once refresh completes.
							</Typography>
						</>
					) : refreshError ? (
						<>
							<Typography variant="body1" color="error" sx={{ mb: 2 }}>
								{refreshError.message}
							</Typography>
							{refreshError.recordingId ? (
								<Typography variant="body2" sx={{ mb: 2 }}>
									Failed on recording <code>{refreshError.recordingId}</code>. No ZIP was downloaded.
								</Typography>
							) : null}
							<Typography variant="body2" color="text.secondary">
								You can retry, or download the ZIP without refreshing waveforms.
							</Typography>
						</>
					) : (
						<>
							<Typography variant="body1" sx={{ mb: 2 }}>
								Include median-beat waveforms in the ZIP for task{" "}
								<strong>"{downloadDialogTask?.name || downloadDialogTask?.id}"</strong>?
							</Typography>
							<Typography variant="body2" sx={{ mb: 1 }}>
								<strong>Refresh and download</strong> re-saves every recording in this task with freshly-computed waveforms, then downloads the ZIP. Recordings saved before this feature was added will not have waveforms otherwise.
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<strong>Download as-is</strong> skips the refresh — the ZIP will only include waveforms for recordings that already had them saved.
							</Typography>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={closeDownloadDialog}
						color="inherit"
						disabled={!!refreshProgress && !refreshError}
					>
						Cancel
					</Button>
					<Button
						onClick={handleDownloadAsIs}
						color="inherit"
						disabled={!!refreshProgress && !refreshError}
					>
						Download as-is
					</Button>
					<Button
						onClick={handleRefreshAndDownload}
						color="primary"
						variant="contained"
						disabled={!!refreshProgress && !refreshError}
					>
						{refreshError ? "Retry refresh" : "Refresh and download"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				aria-labelledby="delete-task-dialog-title"
				aria-describedby="delete-task-dialog-description"
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle id="delete-task-dialog-title">
					Delete Task
				</DialogTitle>
				<DialogContent id="delete-task-dialog-description">
					<Typography variant="body1" sx={{ mb: 2 }}>
						⚠️ Warning: This will delete task <strong>"{taskToDelete?.name}"</strong> forever.
					</Typography>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Please enter <strong>'delete forever'</strong> below to confirm deletion.
					</Typography>
					<TextField
						autoFocus
						fullWidth
						label="Confirm deletion"
						value={confirmText}
						onChange={(e) => setConfirmText(e.target.value)}
						placeholder="delete forever"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDeleteDialog} color="inherit">
						Cancel
					</Button>
					<Button
						onClick={confirmDelete}
						color="error"
						variant="contained"
						disabled={confirmText !== 'delete forever'}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Layout.CenterWrapper>
	)
}

export default TaskList
